class VideoSlider extends HTMLElement {
  constructor() {
    super();
    this.viewport = this.querySelector('.video-slider__viewport');
    this.track = this.querySelector('.video-slider__track');
    this.prevBtn = this.querySelector('.video-slider__nav--prev');
    this.nextBtn = this.querySelector('.video-slider__nav--next');
    this.dots = Array.from(this.querySelectorAll('.video-slider__dot'));
    this.items = Array.from(this.querySelectorAll('.video-slider__item'));
    this.autoplayOnScroll = this.dataset.autoplayScroll === 'true';

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollByCard(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollByCard(1));
    }

    if (this.dots.length) {
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.scrollToIndex(i));
      });
      this.viewport.addEventListener('scroll', () => this.syncActiveDot(), { passive: true });
    }

    this.lockDownVideos();
    this.setupMuteButtons();

    if (this.autoplayOnScroll) {
      this.setupAutoplay();
    } else {
      // Not scroll-gated: just start everything playing immediately.
      this.querySelectorAll('video').forEach((video) => {
        video.play().catch(() => {});
      });
    }
  }

  // Strips out anything that would let the shopper scrub, pause, go
  // fullscreen, download, or otherwise control playback beyond mute/unmute.
  lockDownVideos() {
    this.querySelectorAll('video').forEach((video) => {
      video.removeAttribute('controls');
      video.controls = false;
      video.setAttribute('disablepictureinpicture', '');
      video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback noplaybackrate');
      video.setAttribute('oncontextmenu', 'return false;');
      video.muted = true;
      video.loop = true;
      video.playsInline = true;

      // Belt-and-braces: block any click/tap on the video itself from
      // toggling play/pause (some browsers do this even without the
      // native controls attribute).
      video.addEventListener('click', (e) => e.preventDefault());
    });
  }

  setupMuteButtons() {
    this.querySelectorAll('.video-slider__mute-btn').forEach((btn) => {
      const video = btn.closest('.video-slider__video-wrapper').querySelector('video');
      if (!video) return;

      btn.addEventListener('click', () => {
        video.muted = !video.muted;
        btn.setAttribute('aria-pressed', String(!video.muted));
        btn.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
        btn.classList.toggle('is-unmuted', !video.muted);
      });
    });
  }

  scrollByCard(direction) {
    const item = this.items[0];
    if (!item) return;
    const gap = 16; // matches CSS gap
    const distance = (item.getBoundingClientRect().width + gap) * direction;
    this.viewport.scrollBy({ left: distance, behavior: 'smooth' });
  }

  scrollToIndex(index) {
    const item = this.items[index];
    if (!item) return;
    const viewportRect = this.viewport.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const offset = itemRect.left - viewportRect.left - (viewportRect.width - itemRect.width) / 2;
    this.viewport.scrollBy({ left: offset, behavior: 'smooth' });
  }

  syncActiveDot() {
    const viewportCenter = this.viewport.getBoundingClientRect().left + this.viewport.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;

    this.items.forEach((item, i) => {
      const itemCenter = item.getBoundingClientRect().left + item.clientWidth / 2;
      const distance = Math.abs(itemCenter - viewportCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });

    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === closestIndex));
  }

  setupAutoplay() {
    const videos = this.querySelectorAll('video');
    if (!videos.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            video.play().catch(() => {
              // Autoplay can be blocked by the browser; muted autoplay
              // is allowed almost everywhere, so this is a rare fallback.
            });
          } else {
            video.pause();
          }
        });
      },
      { threshold: [0, 0.6, 1] }
    );

    videos.forEach((video) => observer.observe(video));
  }
}

customElements.define('video-slider', VideoSlider);